import { LotteryJackpotHistoryWinnerDto } from '@app/store/api/historyApi';
import { JackpotSlug, jackpotsOrder } from '@entities/jackpot';
import { LotteryFinishedState, LotteryFrontJackpot } from '@features/LotteryGame/types';

export const getDrawAmount = (
  winTicket: LotteryFrontJackpot | LotteryJackpotHistoryWinnerDto,
) => {
  if ('metadata' in winTicket) return winTicket.metadata.drawAmount;

  if ('drawAmount' in winTicket) {
    const metadata = winTicket.drawAmount;

    const drawAmount =
      metadata && typeof metadata === 'object' && 'drawAmount' in metadata
        ? metadata.drawAmount
        : '';

    return typeof drawAmount === 'string' ? drawAmount : '';
  }
  return '';
};

const groupJackpots = (jackpots: LotteryFrontJackpot[]) => {
  const groupedJackpots = [] as LotteryFrontJackpot[];
  const sortedJackpots = jackpots.sort(
    (a, b) =>
      jackpotsOrder.indexOf(a.jackpotSlug as JackpotSlug) -
      jackpotsOrder.indexOf(b.jackpotSlug as JackpotSlug),
  );

  sortedJackpots.forEach((jackpot) => {
    const existingJackpotIndex = groupedJackpots.findIndex(
      (j) => j.jackpotSlug === jackpot.jackpotSlug,
    );
    if (existingJackpotIndex !== -1) {
      const existingJackpot = groupedJackpots[existingJackpotIndex];
      groupedJackpots[existingJackpotIndex] = {
        ...existingJackpot,
        count: existingJackpot.count + jackpot.count,
      };
    } else {
      groupedJackpots.push(jackpot);
    }
  });

  return groupedJackpots;
};

export const getGroupedWinners = (winners: LotteryFinishedState['winners']) => {
  let splitIndex = 0;

  for (let i = 1; i < winners.length; i++) {
    if (winners[i].player !== winners[i - 1].player) {
      splitIndex = i;
      break;
    }
  }

  const firstPlacedWinnerArray = winners.slice(0, splitIndex);
  const otherWinnersArray = winners.slice(splitIndex);

  const totalFirstWinnerPrize = firstPlacedWinnerArray.reduce((acc, winner) => {
    return acc + (winner.prize?.money ? Number(winner.prize.money) : 0);
  }, 0);

  const jackpots = firstPlacedWinnerArray.reduce((acc, winner) => {
    return [...acc, ...(winner.prize?.jackpots ? winner.prize.jackpots : [])];
  }, [] as LotteryFrontJackpot[]);

  const groupedJackpots = groupJackpots(jackpots);

  const firstPlacedWinner = {
    placesWithWinNumbers: firstPlacedWinnerArray.map((winner, index) => ({
      place: index + 1,
      winNumber: winner.winNumber,
    })),
    winner: firstPlacedWinnerArray[0],
    totalFirstWinnerPrize,
    jackpotsArray: groupedJackpots,
  };

  const otherWinners = otherWinnersArray.map((winner, index) => ({
    place: splitIndex + index + 1,
    winner: winner,
  }));

  return {
    firstPlacedWinner,
    otherWinners,
  };
};

export const getMatchPrizeLabel = (info: {
  orderedMatches: string;
  unorderedMatches: string;
}): string => {
  const hasOrdered = Number(info.orderedMatches) > 0;
  const hasUnordered = Number(info.unorderedMatches) > 0;

  if (hasOrdered && hasUnordered) return 'Ordered Match Prize';
  if (hasOrdered) return 'Order Prize';
  if (hasUnordered) return 'Match Prize';

  return '';
};
